import { FC, useCallback } from 'react';
import { useField } from 'react-final-form';

import {
  DatePicker,
  DatePickerProps,
} from '@beeline/design-system-react/extracted-styles';

import { formatDate } from 'src/utils/formatDate';
import { getDateTime } from 'src/utils/getDateTime';

export interface FormDatePickerProps extends Omit<DatePickerProps, 'value'> {
  name: string;
}

export const FormDatePicker: FC<FormDatePickerProps> = ({
  name,
  required,
  onChange,
  minDate,
  maxDate,
  ...rest
}) => {
  const validate = useCallback(
    (value: string | undefined) => {
      const v = value;

      if (required && !!v) return 'Заполните поле';

      if (!!v && (minDate || maxDate)) {
        const current = getDateTime({
          date: new Date(v),
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        });

        if (minDate) {
          const min = getDateTime({
            date: new Date(minDate),
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          });
          if (current < min) return `Не ранее ${formatDate(min)}`;
        }

        if (maxDate) {
          const max = getDateTime({
            date: new Date(maxDate),
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          });
          if (current > max) return `Не позднее ${formatDate(max)}`;
        }
      }

      return '';
    },
    [maxDate, minDate, required],
  );

  const { input, meta } = useField(name, { type: 'date', validate });
  const error = meta.modified ? meta.error : undefined;

  const handleChange = useCallback(
    (date: string) => {
      input.onChange(date);
      onChange?.(date);
    },
    [input, onChange],
  );

  return (
    <DatePicker
      {...rest}
      value={input.value}
      required={required}
      error={!!error}
      helperText={error}
      onChange={handleChange}
      minDate={minDate}
      maxDate={maxDate}
    />
  );
};
